/**
 * popupQueue — global serializer for app-wide popups.
 *
 * Goal: never show two attention-stealing popups at the same time, and leave
 * a small breathing-room gap between them so the app doesn't feel like a
 * scam landing page when a user completes a goal (celebration → reward →
 * partner offer etc).
 *
 * Mechanics:
 *  - Popups call `acquirePopup(id, priority)` and await the returned promise
 *    before opening. They MUST call `releasePopup(id)` when they close OR
 *    if they decide not to show anymore (cancel).
 *  - Only one popup may hold the slot at a time.
 *  - After a popup releases, the next highest-priority queued popup is
 *    granted the slot after MIN_GAP_MS.
 *  - Higher priority entries jump the queue but never preempt the currently
 *    open popup (we don't want to yank UI from under the user).
 */

const MIN_GAP_MS = 1800;

type Waiter = {
  id: string;
  priority: number;
  resolve: () => void;
};

const queue: Waiter[] = [];
let currentId: string | null = null;
let lastClosedAt = 0;
let timer: ReturnType<typeof setTimeout> | null = null;

function schedule() {
  if (timer || currentId || queue.length === 0) return;
  const wait = Math.max(0, lastClosedAt + MIN_GAP_MS - Date.now());
  timer = setTimeout(() => {
    timer = null;
    if (currentId || queue.length === 0) return;
    queue.sort((a, b) => b.priority - a.priority);
    const next = queue.shift()!;
    currentId = next.id;
    next.resolve();
  }, wait);
}

export function acquirePopup(id: string, priority = 50): Promise<void> {
  return new Promise((resolve) => {
    if (currentId === id) {
      resolve();
      return;
    }
    // Dedupe pending requests by id
    const existing = queue.find((q) => q.id === id);
    if (existing) {
      existing.priority = Math.max(existing.priority, priority);
      existing.resolve = resolve;
    } else {
      queue.push({ id, priority, resolve });
    }
    schedule();
  });
}

export function releasePopup(id: string) {
  if (currentId === id) {
    currentId = null;
    lastClosedAt = Date.now();
  }
  const i = queue.findIndex((q) => q.id === id);
  if (i >= 0) queue.splice(i, 1);
  schedule();
}

/** Convenience: cancel a pending request without affecting the cooldown. */
export function cancelPopup(id: string) {
  const i = queue.findIndex((q) => q.id === id);
  if (i >= 0) queue.splice(i, 1);
  if (currentId === id) {
    currentId = null;
  }
  schedule();
}

export const POPUP_PRIORITIES = {
  /** User just completed a goal — highest priority. */
  celebration: 100,
  /** Reward ready to claim. */
  reward: 80,
  /** Gift received from a connection. */
  gift: 70,
  /** Once-a-day partner offer. */
  dailyOffer: 40,
  /** Daily tip. */
  tip: 20,
} as const;
